import React from 'react';
import * as LucideIcons from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useImpactTypes } from '../hooks/useImpactTypes';
import { useImpactStats } from '../hooks/useImpactStats';
import LoadingSpinner from './LoadingSpinner';

export default function ImpactStats() {
  const { profile } = useAuth();
  const { impactTypes, loading: impactTypesLoading } = useImpactTypes();
  const { stats, loading: statsLoading } = useImpactStats();

  if (!profile || impactTypesLoading || statsLoading) {
    return <LoadingSpinner />;
  }

  const formatValue = (value: number, unit: string) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M${unit ? ` ${unit}` : ''}`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K${unit ? ` ${unit}` : ''}`;
    }
    return `${value.toLocaleString()}${unit ? ` ${unit}` : ''}`;
  };

  // Get personal impact stats from active impact types (excluding total_users and total_missions_completed)
  const personalStats = impactTypes
    .filter(type => 
      type.active && 
      type.name !== 'total_users' && 
      type.name !== 'total_missions_completed' &&
      profile[type.name as keyof typeof profile] !== undefined
    )
    .map(type => {
      const IconComponent = (LucideIcons as any)[type.icon] || LucideIcons.Target;
      return {
        icon: IconComponent,
        label: type.label,
        value: profile[type.name as keyof typeof profile] as number || 0,
        unit: type.unit,
        color: type.color,
        bgColor: type.bg_color
      };
    });

  // Get global impact stats from active impact types (only environmental metrics)
  const globalStats = stats ? impactTypes
    .filter(type => 
      type.active && 
      type.name !== 'total_users' && 
      type.name !== 'total_missions_completed' &&
      stats[type.name as keyof typeof stats] !== undefined
    )
    .slice(0, 3) // Show only first 3 for global stats
    .map(type => {
      const IconComponent = (LucideIcons as any)[type.icon] || LucideIcons.Target;
      return {
        icon: IconComponent,
        label: `Global ${type.label}`,
        value: stats[type.name as keyof typeof stats] as number || 0,
        unit: type.unit,
        color: type.color,
        bgColor: type.bg_color
      };
    }) : [];

  return (
    <div className="space-y-8">
      {/* Personal Impact - Only show if there are active personal impact types */}
      {personalStats.length > 0 && (
        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Environmental Impact</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {personalStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow">
                  <div className={`inline-flex p-3 rounded-lg ${stat.bgColor} mb-4`}>
                    <Icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 mb-1">
                      {formatValue(stat.value, stat.unit)}
                    </p>
                    <p className="text-sm text-gray-600">{stat.label}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Global Impact - Only show if there are active global impact types */}
      {globalStats.length > 0 && stats && (
        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Global Community Impact</h3>
          <div className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-2xl p-8 border border-gray-200">
            <div className="text-center mb-8">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                Together, we're making a real difference
              </h4>
              <p className="text-gray-600">
                Join {stats.total_users.toLocaleString()} eco-warriors around the world creating positive environmental change
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {globalStats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="bg-white rounded-xl p-6 text-center shadow-sm">
                    <div className={`inline-flex p-3 rounded-lg ${stat.bgColor} mb-4`}>
                      <Icon className={`h-6 w-6 ${stat.color}`} />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-gray-900 mb-1">
                        {formatValue(stat.value, stat.unit)}
                      </p>
                      <p className="text-sm text-gray-600">{stat.label}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}